import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  AffiliateApplication,
  AffiliateApplicationStatus,
  RejectAffiliateApplicationDto,
} from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplication } from '../infra/mappers';

interface RejectAffiliateApplicationParams {
  projectId: string;
  applicationId: string;
  sourceStatus: AffiliateApplicationStatus;
  dto: RejectAffiliateApplicationDto;
}

interface UseRejectAffiliateApplicationResult {
  rejectApplication: (params: RejectAffiliateApplicationParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useRejectAffiliateApplication = (): UseRejectAffiliateApplicationResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplication, Error, RejectAffiliateApplicationParams> = useMutation({
    mutationFn: async ({ projectId, applicationId, dto }: RejectAffiliateApplicationParams) => {
      const response = await affiliateApplicationService.rejectApplication(projectId, applicationId, dto);
      return transformAffiliateApplication(response);
    },
    onSuccess: (data, variables) => {
      toast.success('Application rejected');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-applications', variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application', variables.projectId, variables.applicationId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reject application');
    },
  });

  return {
    rejectApplication: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
